// "+ New candidate" (hub, 2026-10-06): a recruiter enters a candidate by hand.
//
// It writes the SAME two rows the rest of the system already reads, so the candidate is a normal one everywhere:
//   1. a public.leads row -- form_type "manual", source_site "manual" -- which the morning brief, second touch and the
//      recruiter agent read (the leads INSERT fires the agent's webhook). recruiter-agent and pt-agents know "manual":
//      a candidate type, texted ONLY with typed sms_consent = true; drafted by second touch only as a second text;
//      never paged "call, don't text" (the recruiter who entered it is the human).
//   2. a public.candidates row shaped exactly like the Promote path's -- stage New Lead, priority Warm, a source tag, and
//      the lead id in the notes -- which the Candidates tab and Onboarding read.
// Consent is the form's checkbox, unchecked by default, written to the TYPED sms_consent column only; raw_payload never
// carries it. A duplicate phone or email (in leads or in candidates) stops the create with a message saying where.
// Nothing is logged but the two new ids.
import { STATE_CODES } from './leadState';

export const MANUAL_FORM_TYPE = 'manual';
export const MANUAL_SOURCE = 'manual';
export const MANUAL_TAG = 'source:manual';
export const NEW_CANDIDATE_DISCIPLINES = ['PT', 'PTA', 'OT', 'COTA', 'SLP'];
export const EMPTY_NEW_CANDIDATE = { firstName: '', lastName: '', discipline: 'PT', email: '', phone: '', state: '', smsConsent: false };

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;
const E164_US = /^\+1[2-9]\d{9}$/;

/** A US mobile as E.164 (+1XXXXXXXXXX), from E.164 or a 10/11-digit US number however it is punctuated; else null. */
export function toE164(phone) {
  const raw = String(phone || '').trim();
  const d = raw.replace(/\D/g, '');
  const e = raw.startsWith('+') ? `+${d}` : d.length === 10 ? `+1${d}` : d.length === 11 && d.startsWith('1') ? `+${d}` : null;
  return e && E164_US.test(e) ? e : null;
}

const last10 = (v) => String(v || '').replace(/\D/g, '').slice(-10);

/** -> { field: message } for every problem; {} when the form can be saved. */
export function validateNewCandidate(f) {
  const errors = {};
  if (!String(f.firstName || '').trim()) errors.firstName = 'First name is required';
  if (!String(f.lastName || '').trim()) errors.lastName = 'Last name is required';
  if (!NEW_CANDIDATE_DISCIPLINES.includes(f.discipline)) errors.discipline = 'Pick a discipline';
  if (!EMAIL_RE.test(String(f.email || '').trim())) errors.email = 'Enter a valid email address';
  if (!toE164(f.phone)) errors.phone = 'Enter a US mobile number, e.g. +1 555 010 0123';
  if (f.state && !STATE_CODES.includes(String(f.state).toUpperCase())) errors.state = 'Pick a state';
  if (typeof f.smsConsent !== 'boolean') errors.smsConsent = 'Consent must be checked or unchecked';
  return errors;
}

/** The leads row. sms_consent is the checkbox, typed; raw_payload carries no consent. */
export function leadRow(f, nowIso) {
  const first = String(f.firstName).trim();
  const last = String(f.lastName).trim();
  return {
    source_site: MANUAL_SOURCE, form_type: MANUAL_FORM_TYPE, source_page: 'crm:new-candidate',
    name: `${first} ${last}`, email: String(f.email).trim().toLowerCase(), phone: toE164(f.phone),
    discipline: f.discipline, state: f.state ? String(f.state).toUpperCase() : null,
    sms_consent: f.smsConsent === true, sms_consent_at: f.smsConsent === true ? nowIso : null,
    raw_payload: { firstName: first, lastName: last, lead_source: MANUAL_SOURCE, entered_via: 'crm' },
  };
}

/** The candidates row, shaped like the Promote path's (App.jsx doPromote): New Lead, Warm, a source tag, the lead id. */
export function candidateRow(lead, nowIso) {
  return {
    name: lead.name, email: lead.email, phone: lead.phone, specialty: lead.discipline, state: lead.state || '',
    stage: 'New Lead', priority: 'Warm', tags: [MANUAL_TAG],
    notes: `Created manually in the CRM · lead_id ${lead.id}`, created_at: nowIso, updated_at: nowIso,
  };
}

export class DuplicateCandidateError extends Error {
  constructor(where, field) {
    super(where === 'candidates'
      ? `A candidate with this ${field} already exists. Open them on the Candidates tab instead.`
      : `A lead with this ${field} already exists. Promote it from the Leads tab instead, so there is one record per person.`);
    this.name = 'DuplicateCandidateError';
    this.where = where;
    this.field = field;
  }
}

/** The first existing record with this email or phone, as { where, field }, else null. Phones compare on their last 10
 *  digits (stored formats vary); the server narrows by the last 4, the client decides. */
export async function findDuplicate(client, { email, phone }) {
  const e = String(email || '').trim().toLowerCase();
  const p10 = last10(phone);
  for (const table of ['candidates', 'leads']) {
    if (e) {
      // ilike for case-insensitive equality; its wildcards (% _) and escape (\) are escaped, so "a_b@x.com" is literal
      const { data, error } = await client.from(table).select('id').ilike('email', e.replace(/[\\%_]/g, '\\$&')).limit(1);
      if (error) throw error;
      if (data && data.length) return { where: table, field: 'email' };
    }
    if (p10.length === 10) {
      const { data, error } = await client.from(table).select('id,phone').ilike('phone', `%${p10.slice(-4)}%`).limit(500);
      if (error) throw error;
      if ((data || []).some((r) => last10(r.phone) === p10)) return { where: table, field: 'phone' };
    }
  }
  return null;
}

/** Validate, refuse a duplicate, then write the lead and the candidate. -> { lead, candidate }. */
export async function createManualCandidate(client, form, now = new Date()) {
  const errors = validateNewCandidate(form);
  if (Object.keys(errors).length) {
    const err = new Error(Object.values(errors)[0]);
    err.fields = errors;
    throw err;
  }
  const nowIso = now.toISOString();
  const dup = await findDuplicate(client, { email: form.email, phone: form.phone });
  if (dup) throw new DuplicateCandidateError(dup.where, dup.field);
  const { data: lead, error: leadErr } = await client.from('leads').insert(leadRow(form, nowIso)).select().single();
  if (leadErr) throw leadErr;
  const { data: candidate, error: candErr } = await client.from('candidates').insert(candidateRow(lead, nowIso)).select().single();
  if (candErr) {
    const err = new Error(`The lead was saved (id ${lead.id}) but the candidate row was not -- Promote it from the Leads tab.`);
    err.lead = lead;
    throw err;
  }
  console.log('manual_candidate_created', lead.id, candidate.id);   // ids only
  return { lead, candidate };
}
